import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { Calendar as CalendarIcon, MapPin, Star, Loader2 } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TravelStop } from '@/hooks/useTravelItineraries';
import { OptimalDateSuggestions } from './OptimalDateSuggestions';

interface PhotonFeature {
  geometry: { coordinates: [number, number] };
  properties: {
    osm_id: number;
    name?: string;
    country?: string;
    state?: string;
    city?: string;
  };
}

function formatPhotonResult(feature: PhotonFeature): string {
  const p = feature.properties;
  const parts = [p.name, p.city ?? p.state, p.country].filter(Boolean);
  return parts.join(', ');
}

interface TripStopDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    location_name: string;
    latitude: number;
    longitude: number;
    arrival_date: string;
    departure_date: string;
    notes?: string;
  }) => void;
  initialData?: TravelStop;
}

interface FavoriteLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  display_name: string | null;
}

export const TripStopDialog: React.FC<TripStopDialogProps> = ({
  open,
  onOpenChange,
  onSave,
  initialData
}) => {
  const { user, isSubscribed } = useAuth();
  const [activeTab, setActiveTab] = useState('favorites');
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState<FavoriteLocation[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);

  // Selected location
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
    latitude: number;
    longitude: number;
  } | null>(initialData ? {
    name: initialData.location_name,
    latitude: Number(initialData.latitude),
    longitude: Number(initialData.longitude)
  } : null);

  // Custom location search
  const [customSearchQuery, setCustomSearchQuery] = useState('');
  const [customSearchResults, setCustomSearchResults] = useState<PhotonFeature[]>([]);
  const [showCustomDropdown, setShowCustomDropdown] = useState(false);
  const [isSearchingCustom, setIsSearchingCustom] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Dates
  const [arrivalDate, setArrivalDate] = useState<Date | undefined>(
    initialData ? parseISO(initialData.arrival_date) : undefined
  );
  const [departureDate, setDepartureDate] = useState<Date | undefined>(
    initialData ? parseISO(initialData.departure_date) : undefined
  );

  // Notes
  const [notes, setNotes] = useState(initialData?.notes || '');

  const [isSaving, setIsSaving] = useState(false);

  // Fetch favorites
  useEffect(() => {
    if (user && open) {
      fetchFavorites();
    }
  }, [user, open]);

  const fetchFavorites = async () => {
    if (!user) return;
    setLoadingFavorites(true);
    try {
      const { data, error } = await supabase
        .from('favorite_locations')
        .select('id, name, latitude, longitude, display_name')
        .eq('user_id', user.id);

      if (error) throw error;
      setFavorites(data || []);
    } catch (err) {
      console.error('Error fetching favorites:', err);
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (initialData) {
        setSelectedLocation({
          name: initialData.location_name,
          latitude: Number(initialData.latitude),
          longitude: Number(initialData.longitude)
        });
        setArrivalDate(parseISO(initialData.arrival_date));
        setDepartureDate(parseISO(initialData.departure_date));
        setNotes(initialData.notes || '');
      } else {
        setSelectedLocation(null);
        setArrivalDate(undefined);
        setDepartureDate(undefined);
        setNotes('');
        setCustomSearchQuery('');
        setCustomSearchResults([]);
        setShowCustomDropdown(false);
      }
    }
  }, [open, initialData]);


  const handleSelectFavorite = (fav: FavoriteLocation) => {
    setSelectedLocation({
      name: fav.display_name || fav.name,
      latitude: Number(fav.latitude),
      longitude: Number(fav.longitude)
    });
  };

  // Debounced search for custom locations
  const searchLocations = async (query: string) => {
    if (query.length < 2) {
      setCustomSearchResults([]);
      setShowCustomDropdown(false);
      return;
    }

    setIsSearchingCustom(true);
    try {
      const response = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      const features: PhotonFeature[] = data.features ?? [];
      setCustomSearchResults(features);
      setShowCustomDropdown(features.length > 0);
    } catch (error) {
      console.error('Error searching locations:', error);
      setCustomSearchResults([]);
    } finally {
      setIsSearchingCustom(false);
    }
  };

  const handleCustomSearchChange = (value: string) => {
    setCustomSearchQuery(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(value);
    }, 300);
  };

  const handleSelectSearchResult = (feature: PhotonFeature) => {
    const [lon, lat] = feature.geometry.coordinates;
    setSelectedLocation({
      name: formatPhotonResult(feature),
      latitude: lat,
      longitude: lon,
    });
    setCustomSearchQuery('');
    setCustomSearchResults([]);
    setShowCustomDropdown(false);
  };

  const canSave = selectedLocation && arrivalDate && departureDate && departureDate >= arrivalDate;

  const handleSave = async () => {
    if (!canSave || !selectedLocation || !arrivalDate || !departureDate) return;
    
    setIsSaving(true);
    try {
      await onSave({
        location_name: selectedLocation.name,
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        arrival_date: format(arrivalDate, 'yyyy-MM-dd'),
        departure_date: format(departureDate, 'yyyy-MM-dd'),
        notes: notes.trim() || undefined
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Stop' : 'Add Stop'}</DialogTitle>
          <DialogDescription>
            Select a location and set your travel dates
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Location Selection */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Location</Label>
            
            {selectedLocation ? (
              <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="font-medium">{selectedLocation.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({selectedLocation.latitude.toFixed(4)}, {selectedLocation.longitude.toFixed(4)})
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedLocation(null)}>
                  Change
                </Button>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="favorites" className="flex-1">My Favorites</TabsTrigger>
                  <TabsTrigger value="custom" className="flex-1">Custom</TabsTrigger>
                </TabsList>

                <TabsContent value="favorites" className="mt-4">
                  {loadingFavorites ? (
                    <p className="text-center text-muted-foreground py-8">Loading...</p>
                  ) : favorites.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No saved locations yet. Add favorites from your Planning Hub.
                    </p>
                  ) : (
                    <ScrollArea className="h-48 border rounded-lg">
                      <div className="p-2 space-y-1">
                        {favorites.map((fav) => (
                          <button
                            key={fav.id}
                            onClick={() => handleSelectFavorite(fav)}
                            className="w-full text-left p-2 hover:bg-muted rounded-md transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4 text-amber-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{fav.display_name || fav.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {Number(fav.latitude).toFixed(4)}, {Number(fav.longitude).toFixed(4)}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>

                <TabsContent value="custom" className="mt-4 space-y-3">
                  <div className="space-y-2 relative">
                    <Label htmlFor="custom-search">Search for a location</Label>
                    <div className="relative">
                      <Input
                        id="custom-search"
                        placeholder="e.g., Farmington, NM"
                        value={customSearchQuery}
                        onChange={(e) => handleCustomSearchChange(e.target.value)}
                        onFocus={() => customSearchResults.length > 0 && setShowCustomDropdown(true)}
                      />
                      {isSearchingCustom && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    
                    {showCustomDropdown && customSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {customSearchResults.map((feature) => (
                          <button
                            key={feature.properties.osm_id}
                            onClick={() => handleSelectSearchResult(feature)}
                            className="w-full text-left p-3 hover:bg-muted transition-colors flex items-center gap-2"
                          >
                            <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="text-sm truncate">{formatPhotonResult(feature)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {customSearchQuery.length > 0 && customSearchQuery.length < 2 && (
                    <p className="text-xs text-muted-foreground">
                      Type at least 2 characters to search
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Arrival Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !arrivalDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {arrivalDate ? format(arrivalDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={arrivalDate}
                    onSelect={setArrivalDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Departure Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !departureDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {departureDate ? format(departureDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={departureDate}
                    onSelect={setDepartureDate}
                    defaultMonth={arrivalDate || undefined}
                    disabled={(date) => arrivalDate ? date < arrivalDate : false}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {departureDate && arrivalDate && departureDate < arrivalDate && (
            <p className="text-sm text-destructive">
              Departure date must be on or after arrival date
            </p>
          )}

          {/* Optimal Date Suggestions — premium only */}
          {isSubscribed && selectedLocation && arrivalDate && departureDate && departureDate >= arrivalDate && (
            <OptimalDateSuggestions
              location={selectedLocation}
              arrivalDate={arrivalDate}
              departureDate={departureDate}
              onSelectWindow={(arrival, departure) => {
                setArrivalDate(arrival);
                setDepartureDate(departure);
              }}
            />
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="stop-notes">Notes (optional)</Label>
            <Textarea
              id="stop-notes"
              placeholder="e.g., Visit Delicate Arch for sunset shots..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving ? 'Saving...' : initialData ? 'Save Changes' : 'Add Stop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
